import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ModaleLocale from "../../../common/CustomModal/ModaleLocale";
import { Pill } from "../../../common/DataList";
import { corpsCorrection, erreursCorrection, optionsCorrection } from "./accompagnement";
import { corrigerSousOrientation } from "./sousOrientationApi";

const Erreur = ({ id, message }) =>
  message ? (
    <p className="correction-so__erreur" id={id} role="alert">
      {message}
    </p>
  ) : null;

// ============================================================
// Modale « Corriger la sous-orientation » (CE-1370, maquette 11) : vers la Caf
// ou un opérateur que l'API propose, avec un motif obligatoire. Les erreurs de
// l'écran et celles de l'API s'affichent sous leur champ.
// ============================================================
const ModaleCorrection = ({ allocataireId, nomAllocataire, so, onFermer, onCorrigee }) => {
  const [vers, setVers] = useState(null);
  const [motif, setMotif] = useState("");
  const [erreurs, setErreurs] = useState({});
  const [refus, setRefus] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const refGroupe = useRef(null);
  const refMotif = useRef(null);
  // Le dossier peut être fermé (navigation) pendant l'enregistrement.
  const monte = useRef(true);
  useEffect(
    () => () => {
      monte.current = false;
    },
    []
  );
  const options = optionsCorrection(so);

  const enregistrer = () => {
    const ecran = erreursCorrection({ vers, motif });
    setErreurs(ecran);
    setRefus(null);
    if (Object.keys(ecran).length > 0) {
      if (ecran.vers) {
        const premier = refGroupe.current && refGroupe.current.querySelector("input:not([disabled])");
        if (premier) premier.focus();
      } else if (refMotif.current) {
        refMotif.current.focus();
      }
      return;
    }
    setEnCours(true);
    corrigerSousOrientation(allocataireId, corpsCorrection(vers, motif)).then(
      (relue) => {
        toast.success("Correction enregistrée dans l'historique.");
        onCorrigee(relue);
      },
      (err) => {
        if (!monte.current) return;
        setEnCours(false);
        if (err && err.champs) {
          setErreurs({ vers: err.champs.operateurId, motif: err.champs.motif });
        } else {
          setRefus((err && err.message) || "La correction n'a pas pu être enregistrée.");
        }
      }
    );
  };

  const pied = (
    <div className="correction-so__pied">
      <button type="button" className="btn btn-ghost" onClick={onFermer} disabled={enCours}>
        Annuler
      </button>
      <button type="button" className="btn btn-primary" onClick={enregistrer} disabled={enCours}>
        Enregistrer la correction
      </button>
    </div>
  );

  return (
    <ModaleLocale
      ouvert
      onFermer={onFermer}
      titre="Corriger la sous-orientation"
      sousTitre={nomAllocataire}
      taille="etroite"
      idTitre="modale-correction-titre"
      peutFermer={!enCours}
      pied={pied}
    >
      <div className="correction-so">
        {refus && (
          <div className="alert alert-danger" role="alert">
            {refus}
          </div>
        )}
        <fieldset
          className="correction-so__groupe"
          aria-describedby={`aide-correction-vers${erreurs.vers ? " erreur-correction-vers" : ""}`}
        >
          <legend>
            Vers <span className="correction-so__requis" aria-hidden="true">*</span>
          </legend>
          <div className="correction-so__choix" ref={refGroupe}>
            {options.map((option) => (
              <label
                key={option.valeur}
                className={`correction-so__carte${vers === option.valeur ? " is-checked" : ""}${option.actuel ? " is-disabled" : ""}`}
              >
                <input
                  type="radio"
                  name="correction-vers"
                  value={option.valeur}
                  checked={vers === option.valeur}
                  disabled={option.actuel}
                  onChange={() => {
                    setVers(option.valeur);
                    setErreurs((e) => ({ ...e, vers: undefined }));
                  }}
                />
                <span>
                  <span className="correction-so__titre">
                    {option.titre}
                    {option.actuel && <Pill tone="muted">Actuel</Pill>}
                  </span>
                  <span className="correction-so__description">{option.description}</span>
                </span>
              </label>
            ))}
          </div>
          {/* Une structure en accompagnement partiel seul n'est pas proposée : le partiel relève de CE-1414. */}
          <p className="correction-so__aide" id="aide-correction-vers">
            Seuls les opérateurs en accompagnement total sont proposés ; l'accompagnement partiel se décide par une
            orientation manuelle.
          </p>
          <Erreur id="erreur-correction-vers" message={erreurs.vers} />
        </fieldset>
        <div className="correction-so__champ">
          <label htmlFor="correction-motif">
            Motif <span className="correction-so__requis" aria-hidden="true">*</span>
          </label>
          <textarea
            id="correction-motif"
            ref={refMotif}
            className="form-control"
            rows={3}
            value={motif}
            onChange={(e) => {
              const valeur = e.target.value;
              setMotif(valeur);
              if (valeur.trim()) setErreurs((courantes) => ({ ...courantes, motif: undefined }));
            }}
            aria-invalid={erreurs.motif ? "true" : undefined}
            aria-describedby={`aide-correction-motif${erreurs.motif ? " erreur-correction-motif" : ""}`}
          />
          <p className="correction-so__aide" id="aide-correction-motif">
            Il reste dans l'historique de l'allocataire, avec votre nom et la date.
          </p>
          <Erreur id="erreur-correction-motif" message={erreurs.motif} />
        </div>
      </div>
    </ModaleLocale>
  );
};

export default ModaleCorrection;
